import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';
import { WalletCategory, DEFAULT_WALLET_CATEGORIES } from '../../types/walletCategory';
import { buildReducers, createRequestStatusObject, RequestStatusObject } from '../utils';

export interface WalletCategoriesState {
	categories: WalletCategory[];
	proxyCategories: WalletCategory[] | null;
	readRequest: RequestStatusObject;
	readProxyRequest: RequestStatusObject;
	createRequest: RequestStatusObject;
	updateRequest: RequestStatusObject;
	deleteRequest: RequestStatusObject;
}

const initialState: WalletCategoriesState = {
	categories: DEFAULT_WALLET_CATEGORIES,
	proxyCategories: null,
	readRequest: createRequestStatusObject(),
	readProxyRequest: createRequestStatusObject(),
	createRequest: createRequestStatusObject(),
	updateRequest: createRequestStatusObject(),
	deleteRequest: createRequestStatusObject()
};

export const fetchWalletCategories = createAsyncThunk<
	WalletCategory[],
	string | undefined,
	{ dispatch: AppDispatch; state: RootState }
>('walletCategories/fetch', async (targetUserId) => {
	const path = targetUserId ? `/users/${targetUserId}/wallet-categories` : '/users/self/wallet-categories';
	const response = await API.get('inspirusapi', path, {});
	return response.walletCategories;
});

export const fetchProxyWalletCategories = createAsyncThunk<
	WalletCategory[],
	{ userId: string },
	{ dispatch: AppDispatch; state: RootState }
>('walletCategories/fetchProxy', async ({ userId }) => {
	const response = await API.get('inspirusapi', `/users/${userId}/wallet-categories`, {});
	return response.walletCategories;
});

export const createWalletCategory = createAsyncThunk<
	WalletCategory[],
	{ label: string },
	{ dispatch: AppDispatch; state: RootState }
>('walletCategories/create', async ({ label }) => {
	const response = await API.post('inspirusapi', '/users/self/wallet-categories', {
		body: { label }
	});
	return response.walletCategories;
});

export const updateWalletCategory = createAsyncThunk<
	WalletCategory[],
	{ categoryId: string; label: string },
	{ dispatch: AppDispatch; state: RootState }
>('walletCategories/update', async ({ categoryId, label }) => {
	const response = await API.put('inspirusapi', `/users/self/wallet-categories/${categoryId}`, {
		body: { label }
	});
	return response.walletCategories;
});

export const deleteWalletCategory = createAsyncThunk<
	WalletCategory[],
	{ categoryId: string },
	{ dispatch: AppDispatch; state: RootState }
>('walletCategories/delete', async ({ categoryId }) => {
	const response = await API.del('inspirusapi', `/users/self/wallet-categories/${categoryId}`, {});
	return response.walletCategories;
});

const walletCategoriesSlice = createSlice({
	name: 'walletCategories',
	initialState,
	reducers: {
		resetWalletCategories: () => initialState,
		clearProxyCategories: (state) => {
			state.proxyCategories = null;
			state.readProxyRequest = createRequestStatusObject();
		}
	},
	extraReducers: (builder) => {
		buildReducers(builder, fetchWalletCategories, 'readRequest', {
			onFulfilled: (state, payload) => {
				if (payload && payload.length > 0) {
					state.categories = payload;
				}
			}
		});
		buildReducers(builder, fetchProxyWalletCategories, 'readProxyRequest', {
			onFulfilled: (state, payload) => {
				if (payload && payload.length > 0) {
					state.proxyCategories = payload;
				}
			}
		});
		buildReducers(builder, createWalletCategory, 'createRequest', {
			onFulfilled: (state, payload) => {
				if (payload) {
					state.categories = payload;
				}
			}
		});
		buildReducers(builder, updateWalletCategory, 'updateRequest', {
			onFulfilled: (state, payload) => {
				if (payload) {
					state.categories = payload;
				}
			}
		});
		buildReducers(builder, deleteWalletCategory, 'deleteRequest', {
			onFulfilled: (state, payload) => {
				if (payload) {
					state.categories = payload;
				}
			}
		});
	}
});

export const { resetWalletCategories, clearProxyCategories } = walletCategoriesSlice.actions;
export default walletCategoriesSlice.reducer;
